import axios from "axios";
import { getToken, clearToken, ADMIN_PATH } from "./auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach admin token to every outgoing request if we have one
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// If server returns 401, our session is gone — clear + bounce to login (unless we're already on public paths)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      const publicPaths = ["/products", "/terms", ADMIN_PATH];
      const cur = window.location.pathname;
      if (!publicPaths.includes(cur)) {
        window.location.href = "/products";
      }
    }
    return Promise.reject(err);
  }
);

export const getCountries = () => api.get("/countries").then((r) => r.data);
export const getStats = () => api.get("/stats").then((r) => r.data);

export const startScan = (kind, country_code) =>
  api.post("/scans/start", { kind, country_code }).then((r) => r.data);
export const getScan = (id) => api.get(`/scans/${id}`).then((r) => r.data);
export const stopScan = (id) => api.post(`/scans/${id}/stop`).then((r) => r.data);
export const listScans = () => api.get("/scans").then((r) => r.data);
export const listActiveScans = () => api.get("/scans/active/list").then((r) => r.data);

export const getBuilders = (params) =>
  api.get("/builders", { params }).then((r) => r.data);
export const updateBuilder = (id, body) =>
  api.patch(`/builders/${id}`, body).then((r) => r.data);
export const deleteBuilder = (id) =>
  api.delete(`/builders/${id}`).then((r) => r.data);

export const getAssociations = (params) =>
  api.get("/associations", { params }).then((r) => r.data);
export const deleteAssociation = (id) =>
  api.delete(`/associations/${id}`).then((r) => r.data);

export const getManufacturers = (params) =>
  api.get("/manufacturers", { params }).then((r) => r.data);
export const deleteManufacturer = (id) =>
  api.delete(`/manufacturers/${id}`).then((r) => r.data);

export const getMatches = (code) =>
  api.get(`/matches/${code}`).then((r) => r.data);

export const generateOutreach = (association_id, opts = {}) =>
  api.post("/outreach/generate", { association_id, ...opts }).then((r) => r.data);

export const pitchInstaller = (installer_id, opts = {}) =>
  api.post("/outreach/pitch-installer", { installer_id, ...opts }).then((r) => r.data);

export const sendOne = (payload) =>
  api.post("/outreach/send-one", payload).then((r) => r.data);

export const sendBatch = (payload) =>
  api.post("/outreach/send-batch", payload).then((r) => r.data);

export const getBatch = (id) =>
  api.get(`/outreach/batch/${id}`).then((r) => r.data);

export const stopBatch = (id) =>
  api.post(`/outreach/batch/${id}/stop`).then((r) => r.data);

export const getSendStatus = () =>
  api.get("/outreach/status").then((r) => r.data);

export const getSentHistory = (params = {}) =>
  api.get("/outreach/sent", { params }).then((r) => r.data);

export const updateSent = (id, payload) =>
  api.patch(`/outreach/sent/${id}`, payload).then((r) => r.data);

export const deleteSent = (id) =>
  api.delete(`/outreach/sent/${id}`).then((r) => r.data);

export const generateFollowup = (payload) =>
  api.post("/outreach/followup/generate", payload).then((r) => r.data);

export const sendFollowup = (payload) =>
  api.post("/outreach/followup/send", payload).then((r) => r.data);

export const exportBuildersUrl = (code) =>
  `${API}/builders/export${code ? `?country_code=${code}` : ""}`;
export const exportAssociationsUrl = (code) =>
  `${API}/associations/export${code ? `?country_code=${code}` : ""}`;
export const exportManufacturersUrl = (code) =>
  `${API}/manufacturers/export${code ? `?country_code=${code}` : ""}`;

// ---------- Invoicing (replaces Stripe) ----------
export const getInvoiceCatalog = () =>
  api.get("/invoices/catalog").then((r) => r.data);
export const getSellerInfo = () =>
  api.get("/invoices/seller").then((r) => r.data);
export const createInvoice = (payload) =>
  api.post("/invoices/create", payload).then((r) => r.data);
export const listInvoices = (params = {}) =>
  api.get("/invoices", { params }).then((r) => r.data);
export const getInvoice = (id) =>
  api.get(`/invoices/${id}`).then((r) => r.data);
export const updateInvoice = (id, payload) =>
  api.patch(`/invoices/${id}`, payload).then((r) => r.data);
export const resendInvoice = (id) =>
  api.post(`/invoices/${id}/resend`).then((r) => r.data);
export const deliverInvoice = (id) =>
  api.post(`/invoices/${id}/deliver`).then((r) => r.data);
export const deleteInvoice = (id) =>
  api.delete(`/invoices/${id}`).then((r) => r.data);
export const invoicePdfUrl = (id) => `${API}/invoices/${id}/pdf`;

// ---------- Autopilot (daily auto-send scheduler) ----------
export const getAutopilotStatus = () =>
  api.get("/autopilot/status").then((r) => r.data);
export const updateAutopilotConfig = (payload) =>
  api.post("/autopilot/config", payload).then((r) => r.data);
export const runAutopilotNow = () =>
  api.post("/autopilot/run-now").then((r) => r.data);
export const resetAutopilot = () =>
  api.post("/autopilot/reset").then((r) => r.data);
